import { Controller } from "@hotwired/stimulus"

/**
 * ScreenLockController - Sistema unificado de bloqueio de ecrã
 *
 * Bloqueia completamente todas as interações durante requisições:
 * - Previne clicks, hovers, scrolls
 * - Mostra overlay visual com efeito "magia fosca"
 * - Suporta múltiplas requisições simultâneas
 *
 * Uso:
 *   window.screenLock.lock("request-id", "Mensagem...")
 *   window.screenLock.unlock("request-id")
 */
export default class extends Controller {
  static values = {
    locked: { type: Boolean, default: false }
  }

  connect() {
    // Registrar como singleton global
    window.screenLock = this

    // Estado de requisições ativas
    this.activeRequests = new Set()

    // Criar overlay
    this.createOverlay()

    // Bound handlers para remoção posterior
    this.boundPreventEvent = this.preventEvent.bind(this)
    this.boundPreventKey = this.preventKey.bind(this)
  }

  disconnect() {
    delete window.screenLock
    this.unlock()
    this.removeOverlay()
  }

  createOverlay() {
    // Verificar se já existe
    if (document.getElementById("screen-lock-overlay")) {
      this.overlay = document.getElementById("screen-lock-overlay")
      this.messageEl = this.overlay.querySelector(".screen-lock-message")
      return
    }

    this.overlay = document.createElement("div")
    this.overlay.id = "screen-lock-overlay"
    this.overlay.className = "screen-lock-overlay"
    this.overlay.setAttribute("aria-hidden", "true")
    this.overlay.setAttribute("role", "alert")
    this.overlay.setAttribute("aria-live", "polite")

    this.overlay.innerHTML = `
      <div class="screen-lock-content">
        <div class="screen-lock-spinner">
          <div class="screen-lock-ring"></div>
          <div class="screen-lock-ring"></div>
          <div class="screen-lock-ring"></div>
          <div class="screen-lock-glow"></div>
        </div>
        <div class="screen-lock-message" aria-live="polite"></div>
      </div>
    `

    document.body.appendChild(this.overlay)
    this.messageEl = this.overlay.querySelector(".screen-lock-message")
  }

  removeOverlay() {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay)
    }
  }

  // ─────────────────────────────────────────────────────────────
  // API Pública
  // ─────────────────────────────────────────────────────────────

  /**
   * Bloqueia o ecrã
   * @param {string} requestId - ID único da requisição (opcional)
   * @param {string} message - Mensagem a mostrar (opcional)
   * @param {number} maxDuration - Duração máxima em ms antes de auto-unlock (default: 30s)
   * @returns {string} - ID da requisição (para usar no unlock)
   */
  lock(requestId = null, message = null, maxDuration = 30000) {
    const id = requestId || `lock-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    this.activeRequests.add(id)

    // Atualizar mensagem se fornecida
    if (message && this.messageEl) {
      this.messageEl.textContent = message
    }

    // Ativar bloqueio se ainda não ativo
    if (!this.lockedValue) {
      this.lockedValue = true
      this.enableLock()
    }

    // Safety timeout: auto-unlock after maxDuration to prevent stuck loaders
    if (maxDuration > 0) {
      const timeoutId = window.setTimeout(() => {
        if (this.activeRequests.has(id)) {
          console.warn(`[ScreenLock] Auto-unlocking ${id} after ${maxDuration}ms timeout`)
          this.unlock(id)
        }
      }, maxDuration)
      
      // Store timeout so it can be cleared on manual unlock
      if (!this.lockTimeouts) this.lockTimeouts = new Map()
      this.lockTimeouts.set(id, timeoutId)
    }

    return id
  }

  /**
   * Desbloqueia o ecrã
   * @param {string} requestId - ID da requisição a remover (opcional)
   */
  unlock(requestId = null) {
    if (requestId) {
      this.activeRequests.delete(requestId)
    } else {
      // Sem ID, limpar tudo
      this.activeRequests.clear()
    }

    // Só desbloquear se não houver mais requisições ativas
    if (this.activeRequests.size === 0 && this.lockedValue) {
      this.lockedValue = false
      this.disableLock()
    }
  }

  /**
   * Atualiza a mensagem sem alterar o estado de bloqueio
   * @param {string} message - Nova mensagem
   */
  updateMessage(message) {
    if (this.messageEl) {
      this.messageEl.textContent = message
    }
  }

  /**
   * Força desbloqueio (para debug/emergência)
   */
  forceUnlock() {
    console.warn("[ScreenLock] Force unlocking screen...")
    this.activeRequests.clear()
    this.lockedValue = false
    this.disableLock()
  }

  /**
   * Verifica se está bloqueado
   * @returns {boolean}
   */
  isLocked() {
    return this.lockedValue
  }

  // ─────────────────────────────────────────────────────────────
  // Métodos Internos
  // ─────────────────────────────────────────────────────────────

  enableLock() {
    if (!this.overlay) return

    // Prevenir scroll no body
    document.body.style.overflow = "hidden"
    document.body.dataset.screenLocked = "true"

    // Mostrar overlay
    this.overlay.classList.add("screen-lock-overlay--active")
    this.overlay.setAttribute("aria-hidden", "false")

    // Desabilitar elementos interativos
    this.disableInteractions()

    // Adicionar listeners para prevenir eventos
    this.addEventPrevention()

    // Trigger animação após um frame
    requestAnimationFrame(() => {
      this.overlay.classList.add("screen-lock-overlay--visible")
    })
  }

  disableLock() {
    if (!this.overlay) return

    // Restaurar scroll
    document.body.style.overflow = ""
    delete document.body.dataset.screenLocked

    // Esconder overlay com animação
    this.overlay.classList.remove("screen-lock-overlay--visible")

    // Aguardar animação antes de remover
    setTimeout(() => {
      this.overlay.classList.remove("screen-lock-overlay--active")
      this.overlay.setAttribute("aria-hidden", "true")
    }, 300)

    // Reabilitar interações
    this.enableInteractions()

    // Remover listeners
    this.removeEventPrevention()

    // Reset mensagem
    if (this.messageEl) {
      this.messageEl.textContent = ""
    }
  }

  disableInteractions() {
    // Guardar referência aos elementos que vamos desabilitar
    this.interactiveElements = document.querySelectorAll(
      'button:not([data-screen-lock-ignore]):not(:disabled), ' +
      'a[href]:not([data-screen-lock-ignore]):not([disabled]), ' +
      'input:not([data-screen-lock-ignore]):not(:disabled), ' +
      'textarea:not([data-screen-lock-ignore]):not(:disabled), ' +
      'select:not([data-screen-lock-ignore]):not(:disabled), ' +
      '[tabindex]:not([tabindex="-1"]):not([data-screen-lock-ignore]):not(:disabled), ' +
      '[data-action]:not([data-screen-lock-ignore]):not([disabled])'
    )

    this.interactiveElements.forEach(el => {
      // Guardar estado anterior
      el.dataset.screenLockWasDisabled = el.disabled || "false"
      el.dataset.screenLockWasTabindex = el.getAttribute("tabindex") || ""

      // Desabilitar
      if (el.tagName !== "A") {
        el.disabled = true
      }
      el.setAttribute("tabindex", "-1")
      el.setAttribute("aria-disabled", "true")
    })
  }

  enableInteractions() {
    if (!this.interactiveElements) return

    this.interactiveElements.forEach(el => {
      // Restaurar estado anterior
      if (el.dataset.screenLockWasDisabled !== "true") {
        el.disabled = false
      }

      const previousTabindex = el.dataset.screenLockWasTabindex
      if (previousTabindex) {
        el.setAttribute("tabindex", previousTabindex)
      } else {
        el.removeAttribute("tabindex")
      }

      el.removeAttribute("aria-disabled")

      // Limpar atributos temporários
      delete el.dataset.screenLockWasDisabled
      delete el.dataset.screenLockWasTabindex
    })

    this.interactiveElements = null
  }

  addEventPrevention() {
    // Capturar na fase de captura para bloquear antes de qualquer handler
    const options = { capture: true, passive: false }

    document.addEventListener("click", this.boundPreventEvent, options)
    document.addEventListener("mousedown", this.boundPreventEvent, options)
    document.addEventListener("mouseup", this.boundPreventEvent, options)
    document.addEventListener("touchstart", this.boundPreventEvent, options)
    document.addEventListener("touchend", this.boundPreventEvent, options)
    document.addEventListener("touchmove", this.boundPreventEvent, options)
    document.addEventListener("contextmenu", this.boundPreventEvent, options)
    document.addEventListener("dblclick", this.boundPreventEvent, options)
    document.addEventListener("pointerdown", this.boundPreventEvent, options)
    document.addEventListener("pointerup", this.boundPreventEvent, options)
    document.addEventListener("keydown", this.boundPreventKey, options)
    document.addEventListener("keyup", this.boundPreventKey, options)
    document.addEventListener("keypress", this.boundPreventKey, options)
  }

  removeEventPrevention() {
    const options = { capture: true, passive: false }

    document.removeEventListener("click", this.boundPreventEvent, options)
    document.removeEventListener("mousedown", this.boundPreventEvent, options)
    document.removeEventListener("mouseup", this.boundPreventEvent, options)
    document.removeEventListener("touchstart", this.boundPreventEvent, options)
    document.removeEventListener("touchend", this.boundPreventEvent, options)
    document.removeEventListener("touchmove", this.boundPreventEvent, options)
    document.removeEventListener("contextmenu", this.boundPreventEvent, options)
    document.removeEventListener("dblclick", this.boundPreventEvent, options)
    document.removeEventListener("pointerdown", this.boundPreventEvent, options)
    document.removeEventListener("pointerup", this.boundPreventEvent, options)
    document.removeEventListener("keydown", this.boundPreventKey, options)
    document.removeEventListener("keyup", this.boundPreventKey, options)
    document.removeEventListener("keypress", this.boundPreventKey, options)
  }

  preventEvent(e) {
    // Permitir eventos dentro do overlay (para acessibilidade)
    if (this.overlay && this.overlay.contains(e.target)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
    return false
  }

  preventKey(e) {
    // Permitir Tab para acessibilidade dentro do overlay
    if (e.key === "Tab" && this.overlay && this.overlay.contains(document.activeElement)) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
    return false
  }
}
